// 性能优化（FCP/LCP）：轻量"环境/设备识别" helper。
//
// 抽出动机：之前 modules/pwa/init 通过 device-helpers → utils.ts 拉入
// 整份 utils.ts（2000+ 行）+ axios/crypto-js/i18n 等传递依赖，
// 让 main / landing entry chunk 评估多花 100-200ms（直接命中 LCP）。
// 本文件仅依赖 storage-helpers 与 appChannel（LtProxy），体积可控。
//
// 单一来源（重要）：utils.ts 中的同名公开方法（getEnvInfo / isApp /
// updateCheckIsPwa / supportPwa / checkIsIos / isSafari /
// checkPwaEverInstalled / judgeClient / isMobile / isDevicePC）已经改为
// 薄代理，全部回调到本文件。本文件就是这些方法的唯一真实实现，修改时
// 不需要同步任何其他文件。任何调用方（无论走 `utils.getEnvInfo()` 还是
// `import { getEnvInfo } from "@/utils/env-helpers"` 或 `import { ... }
// from "@/utils/device-helpers"`）都执行同一份代码，永远不会出现行为漂移。
import { LtProxy } from "./appChannel";
import {
  getObject,
  getTmpObject,
  setObject,
  setTmpObject,
} from "./storage-helpers";

export interface EnvInfo {
  os: "iOS" | "Android" | "Other";
  browser: string;
  context: "InAppWebView" | "ExternalBrowser" | "NativeApp";
  referrer: string;
  isChrome: boolean;
  isWebView: boolean;
  isNativeApp: boolean;
  isTelegram: boolean;
  isTikTok: boolean;
}

// utils.ts 的 getQueryString 接受可选 location 参数，本文件内部使用
// 固定走 window.location 即可，避免重复实现 Location 解析的复杂度。
function getQueryString(name: string): string | null {
  const reg = new RegExp("(^|&)" + name + "=([^&]*)(&|$)", "i");
  const r = window.location.search.substr(1).match(reg);
  if (r != null) return r[2];
  return null;
}

export function isApp(): boolean {
  return (
    !!LtProxy.isActive() ||
    !!getObject("gaid") ||
    !!getObject("aid") ||
    !!getObject("inApp")
  );
}

export function getEnvInfo(): EnvInfo {
  const ua = (navigator?.userAgent || "").toLowerCase();
  const isAndroid = /android/.test(ua);
  const isiOS = /iphone|ipad|ipod/.test(ua);

  // 最先判断：APK 原生 App 环境
  // APK 内嵌 WebView 的 UA 也包含 wv / window.android 等标记，但不应被视为 InAppWebView。
  // isApp() 依赖 localStorage（gaid/aid）或 bridge 对象，首次加载时可能还没初始化，
  // 因此额外检查 URL 参数和 flutter/RN bridge 作为补充。
  const isNativeApp =
    isApp() ||
    !!getQueryString("gaid") ||
    !!getQueryString("aid") ||
    !!getQueryString("idfv") ||
    !!getQueryString("idfa") ||
    typeof (window as any).flutter_inappwebview !== "undefined" ||
    typeof (window as any).ReactNativeWebView !== "undefined";

  const inFB = /fbav|fban|fb_iab/.test(ua);
  const inIG = /instagram/.test(ua);
  const inWeChat = /micromessenger/.test(ua);
  const inLine = /line\//.test(ua);
  const inTikTok =
    /tiktok/.test(ua) ||
    /musical_ly/.test(ua) ||
    /aweme/.test(ua) ||
    /bytedancewebview/.test(ua);
  const inTelegram =
    /telegram|tga|twa/.test(ua) ||
    /t\.me|telegram\.org/.test(document.referrer || "") ||
    !!(window as any).TelegramWebviewProxy ||
    !!(window as any).TelegramWebApp ||
    !!(window as any).Telegram?.WebApp;

  // 使用 WebView 组件但属于独立浏览器，不应识别为 in-app WebView
  const isKnownBrowserUsingWebView =
    /hibrowser|holabrowser|hola/i.test(ua) || // Hola Browser
    /ucbrowser|ucweb/i.test(ua) || // UC Browser
    /brave/i.test(ua) || // Brave
    /duckduckgo/i.test(ua) || // DuckDuckGo
    /mint/i.test(ua); // Mint Browser

  const isWebView =
    !isNativeApp &&
    !isKnownBrowserUsingWebView &&
    (/; wv\)/.test(ua) || // Android WebView 标记
      /\bwv\b/.test(ua) || // WebView 标记
      /version\/\d+.*chrome/.test(ua) || // Android WebView 特征
      // iOS 仅使用明确桥接特征，避免把 Safari/Chrome 误判为 WebView
      !!(window as any).webkit?.messageHandlers?.openExternalBrowser ||
      !!(window as any).webkit?.messageHandlers?.openInBrowser ||
      !!(window as any).flutter_inappwebview || // Flutter WebView
      !!(window as any).ReactNativeWebView || // React Native WebView
      !!(window as any).android || // Android WebView bridge
      !!(window as any).AndroidBridge); // 自定义 Android bridge

  // 所有已知的应用内浏览器（APK 原生环境不算）
  const inKnownAppWebView =
    !isNativeApp &&
    (inFB || inIG || inWeChat || inLine || inTikTok || inTelegram);

  const inAppWebView = inKnownAppWebView || isWebView;

  const isEdge = /edg\//.test(ua);
  const isOpera = /opr\//.test(ua) || /opera/.test(ua);
  const isSamsung = /samsungbrowser/.test(ua);
  const isFirefox = /firefox/.test(ua);
  const isChromeToken = /chrome\/\d+/.test(ua) || /crios/.test(ua);
  const isChrome =
    isChromeToken &&
    !isEdge &&
    !isOpera &&
    !isSamsung &&
    !isWebView &&
    !isKnownBrowserUsingWebView;

  let browser = "Other";
  if (isNativeApp) browser = "NativeApp";
  else if (inFB) browser = "FacebookInApp";
  else if (inIG) browser = "InstagramInApp";
  else if (inWeChat) browser = "WeChatInApp";
  else if (inLine) browser = "LineInApp";
  else if (inTikTok) browser = "TikTokInApp";
  else if (inTelegram) browser = "TelegramInApp";
  else if (isWebView) browser = "UnknownWebView";
  else if (isKnownBrowserUsingWebView) browser = "ThirdPartyBrowser";
  else if (isSamsung) browser = "Samsung";
  else if (isEdge) browser = "Edge";
  else if (isOpera) browser = "Opera";
  else if (isFirefox) browser = "Firefox";
  else if (isChrome) browser = "Chrome";
  else if (/safari/.test(ua)) browser = "Safari";

  const context: EnvInfo["context"] = isNativeApp
    ? "NativeApp"
    : inAppWebView
      ? "InAppWebView"
      : "ExternalBrowser";

  return {
    os: isiOS ? "iOS" : isAndroid ? "Android" : "Other",
    browser,
    context,
    referrer: document?.referrer || "",
    isChrome,
    isWebView,
    isNativeApp,
    isTelegram: inTelegram,
    isTikTok: inTikTok,
  };
}

export function supportPwa(): boolean {
  let flag = getObject("supportPwa");
  if (flag) return flag;
  flag = !!(window as any).deferredPrompt;
  if (flag) {
    setObject("supportPwa", true);
  }
  return flag;
}

// 与 utils.updateCheckIsPwa 实现保持一致：
// 1) 先排除 InAppWebView（standalone display-mode 在 IG/WhatsApp 等 in-app
//    WebView 里也可能为 true，会误判）
// 2) tmp("pwa") 短路：之前请求里 set 过就直接返回
// 3) ?pwa=true 强制识别（用于跨域/落地页跳转标记）
// 4) display-mode 或 navigator.standalone
export function updateCheckIsPwa(): boolean {
  try {
    const envInfo = getEnvInfo();
    if (envInfo.context === "InAppWebView") {
      return false;
    }
  } catch {
    // ignore
  }

  const pwa = getTmpObject("pwa");
  if (pwa) return true;

  const pp = getQueryString("pwa");
  if ("true" === pp) {
    setTmpObject("pwa", true);
    return true;
  }
  try {
    if (
      window.matchMedia("(display-mode: standalone)").matches ||
      (window as any).navigator.standalone === true
    ) {
      setTmpObject("pwa", true);
      return true;
    }
  } catch (err) {
    console.error(err);
  }
  return false;
}

// =============================================================
// 设备/浏览器纯检测函数（迁移自 utils.ts，作为唯一实现）
// -------------------------------------------------------------
// 这些函数原先重复实现在 utils.ts 类方法和 device-helpers.ts 内联函数
// 两处，存在"改一处忘改另一处"的漂移风险。统一下沉到 env-helpers.ts，
// 让 utils.ts 类方法和 device-helpers.ts 都薄代理回这里。
// =============================================================

export function checkIsIos(): boolean {
  return (
    [
      "iPad Simulator",
      "iPhone Simulator",
      "iPod Simulator",
      "iPad",
      "iPhone",
      "iPod",
    ].includes(navigator.platform) ||
    (navigator.userAgent.includes("Mac") && navigator.maxTouchPoints > 1)
  );
}

export function isSafari(): boolean {
  try {
    const ua = (navigator?.userAgent || "").toLowerCase();

    const inAppBrowser =
      /fbav|fban|fb_iab/.test(ua) ||
      /instagram/.test(ua) ||
      /micromessenger/.test(ua) ||
      /line\//.test(ua) ||
      /tiktok|musical_ly|aweme|bytedancewebview/.test(ua) ||
      /telegram|tga|twa/.test(ua);

    const isWebView =
      /; wv\)/.test(ua) ||
      /\bwv\b/.test(ua) ||
      /version\/\d+.*chrome/.test(ua) ||
      !!(window as any).webkit?.messageHandlers?.openExternalBrowser ||
      !!(window as any).webkit?.messageHandlers?.openInBrowser ||
      !!(window as any).flutter_inappwebview ||
      !!(window as any).ReactNativeWebView ||
      !!(window as any).android ||
      !!(window as any).AndroidBridge;

    if (inAppBrowser || isWebView) return false;

    const hasSafari = /safari/.test(ua);
    const hasChrome = /chrome/.test(ua) || /crios/.test(ua);

    return hasSafari && !hasChrome;
  } catch {
    return false;
  }
}

export function checkPwaEverInstalled(): boolean {
  try {
    const installed = localStorage.getItem("pwa_ever_installed");
    return installed === "true";
  } catch (e) {
    console.error("检查PWA安装标记失败:", e);
    return false;
  }
}

export function judgeClient(): "Android" | "IOS" | "PC" {
  const u = navigator.userAgent;
  const isAndroid = u.indexOf("Android") > -1 || u.indexOf("Adr") > -1;
  const isIOS = !!u.match(/\(i[^;]+;( U;)? CPU.+Mac OS X/);
  if (isAndroid) return "Android";
  if (isIOS) return "IOS";
  return "PC";
}

// 与 utils.isMobile 行为等价：utils 类里 `forceMobileLayout` 默认 true 且
// 代码中没有任何位置改写它，所以原实现 `if (this.forceMobileLayout) return true`
// 等价于直接返回 true。
export function isMobile(): boolean {
  return true;
}

export function isDevicePC(): boolean {
  const userAgent = navigator.userAgent.toLowerCase();

  const mobileKeywords = [
    "android",
    "webos",
    "iphone",
    "ipad",
    "ipod",
    "blackberry",
    "windows phone",
    "mobile",
    "tablet",
  ];

  const isMobileDevice = mobileKeywords.some(k => userAgent.includes(k));
  const isTablet =
    userAgent.includes("ipad") ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

  if (isMobileDevice || isTablet) {
    const screenWidth = window.screen.width;
    const screenHeight = window.screen.height;
    if (screenWidth > 1024 || screenHeight > 1024) return true;
    return false;
  }
  return true;
}
